import { useState, useMemo, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  BarChart3, Building2, User, Search,
  ArrowUpDown, ArrowUp, ArrowDown, Users, TrendingUp, Award,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  learningReportApi,
  type AdminOrganizationProgress,
  type AdminLearnerProgress,
} from '@/api/learningReport'
import { LearnerProgressDetailModal } from '@/components/LearnerProgressDetailModal'

type TabType = 'organization' | 'learner'

export function LearningStatusPage() {
  const [activeTab, setActiveTab] = useState<TabType>('organization')
  const [selectedOrgId, setSelectedOrgId] = useState<number | undefined>()

  const handleNavigateToLearnersByOrg = (orgId: number) => {
    setSelectedOrgId(orgId)
    setActiveTab('learner')
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
          <BarChart3 className="w-7 h-7 text-amber-500" />
          학습 현황
        </h1>
        <p className="mt-1 text-slate-500">전체 조직의 학습 진행 상황을 다양한 관점에서 확인하세요.</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('organization')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'organization'
                  ? 'border-amber-500 text-amber-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Building2 className="w-4 h-4" />
                <span>조직별</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('learner')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'learner'
                  ? 'border-amber-500 text-amber-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4" />
                <span>학습자별</span>
              </div>
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'organization' && (
            <OrganizationView onNavigateToLearners={handleNavigateToLearnersByOrg} />
          )}
          {activeTab === 'learner' && (
            <LearnerView
              initialOrgId={selectedOrgId}
              onOrgIdConsumed={() => setSelectedOrgId(undefined)}
            />
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Organization View
// ============================================================================

function OrganizationView({ onNavigateToLearners }: { onNavigateToLearners: (orgId: number) => void }) {
  const { data: orgData, isLoading } = useQuery({
    queryKey: ['adminOrgProgress'],
    queryFn: () => learningReportApi.getOrganizationProgress(),
  })

  const organizations: AdminOrganizationProgress[] = orgData?.data?.data || []

  // 전체 요약 계산
  const summary = useMemo(() => {
    if (organizations.length === 0) return null
    const totalLearners = organizations.reduce((sum, o) => sum + o.totalLearners, 0)
    const totalCompleted = organizations.reduce((sum, o) => sum + o.completedCount, 0)
    const totalInProgress = organizations.reduce((sum, o) => sum + o.inProgressCount, 0)
    const totalNotStarted = organizations.reduce((sum, o) => sum + o.notStartedCount, 0)
    const avgProgress = organizations.length > 0
      ? organizations.reduce((sum, o) => sum + o.averageProgressRate, 0) / organizations.length
      : 0
    const totalAssignments = totalCompleted + totalInProgress + totalNotStarted
    const completionRate = totalAssignments > 0 ? (totalCompleted / totalAssignments) * 100 : 0
    return { totalLearners, totalCompleted, totalInProgress, totalNotStarted, avgProgress, completionRate }
  }, [organizations])

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    )
  }

  if (organizations.length === 0) {
    return <div className="py-8 text-center text-gray-500">조직 학습 현황 데이터가 없습니다.</div>
  }

  const orgTypeLabel = (type: string) => {
    switch (type) {
      case 'company': return '기업'
      case 'learning_center': return '러닝센터'
      default: return type
    }
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div className="p-4 bg-gray-50 rounded-xl">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-gray-400" />
              <p className="text-xs text-gray-500">전체 학습자</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{summary.totalLearners.toLocaleString()}명</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-xl">
            <div className="flex items-center gap-2 mb-1">
              <Award className="w-4 h-4 text-green-500" />
              <p className="text-xs text-gray-500">수료율</p>
            </div>
            <p className="text-2xl font-bold text-green-600">{summary.completionRate.toFixed(1)}%</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-xl">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-amber-500" />
              <p className="text-xs text-gray-500">평균 진도율</p>
            </div>
            <p className="text-2xl font-bold text-amber-600">{summary.avgProgress.toFixed(1)}%</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-xl">
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="w-4 h-4 text-gray-400" />
              <p className="text-xs text-gray-500">조직 수</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{organizations.length}개</p>
          </div>
        </div>
      )}

      {/* Organization Table */}
      <div className="overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase">조직명</th>
                <th className="px-4 py-3 text-xs font-medium text-center text-gray-500 uppercase whitespace-nowrap">유형</th>
                <th className="px-4 py-3 text-xs font-medium text-center text-gray-500 uppercase whitespace-nowrap">학습자 수</th>
                <th className="px-4 py-3 text-xs font-medium text-center text-gray-500 uppercase whitespace-nowrap">미시작</th>
                <th className="px-4 py-3 text-xs font-medium text-center text-gray-500 uppercase whitespace-nowrap">수강중</th>
                <th className="px-4 py-3 text-xs font-medium text-center text-gray-500 uppercase whitespace-nowrap">수료</th>
                <th className="px-4 py-3 text-xs font-medium text-center text-gray-500 uppercase whitespace-nowrap">평균 진도율</th>
                <th className="px-4 py-3 text-xs font-medium text-center text-gray-500 uppercase whitespace-nowrap">수료율</th>
                <th className="px-4 py-3 text-xs font-medium text-center text-gray-500 uppercase whitespace-nowrap">클래스</th>
                <th className="px-4 py-3 text-xs font-medium text-center text-gray-500 uppercase whitespace-nowrap">액션</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {organizations.map((org) => (
                <tr key={org.organizationId} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{org.organizationName}</div>
                      {org.subdomain && (
                        <div className="text-xs text-gray-400">{org.subdomain}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                      org.organizationType === 'company'
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'bg-teal-100 text-teal-700'
                    }`}>
                      {orgTypeLabel(org.organizationType)}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-center text-gray-900 font-medium">{org.totalLearners}명</td>
                  <td className="px-4 py-4 text-center">
                    <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
                      {org.notStartedCount}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                      {org.inProgressCount}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
                      {org.completedCount}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center justify-center space-x-2">
                      <div className="overflow-hidden w-16 h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-full bg-amber-500 rounded-full transition-all duration-500"
                          style={{ width: `${Math.min(org.averageProgressRate, 100)}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 w-10 text-right">{org.averageProgressRate.toFixed(1)}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center justify-center space-x-2">
                      <div className="overflow-hidden w-16 h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-full bg-green-500 rounded-full transition-all duration-500"
                          style={{ width: `${Math.min(org.completionRate, 100)}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 w-10 text-right">{org.completionRate.toFixed(1)}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-center text-gray-500">
                    {org.activeClasses}/{org.totalClasses}
                  </td>
                  <td className="px-4 py-4 text-center">
                    <button
                      onClick={() => onNavigateToLearners(org.organizationId)}
                      className="px-3 py-1 text-xs font-medium text-amber-600 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors"
                    >
                      학습자 보기
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Learner View
// ============================================================================

function LearnerView({ initialOrgId, onOrgIdConsumed }: {
  initialOrgId?: number
  onOrgIdConsumed?: () => void
}) {
  const [searchKeyword, setSearchKeyword] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [selectedOrgId, setSelectedOrgId] = useState<number | undefined>(initialOrgId)
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [sortField, setSortField] = useState<'fullName' | 'email' | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [selectedLearner, setSelectedLearner] = useState<{
    userId: number; classProductId: number; fullName: string; className: string
  } | null>(null)

  // initialOrgId 변경 시 적용
  useEffect(() => {
    if (initialOrgId !== undefined) {
      setSelectedOrgId(initialOrgId)
      onOrgIdConsumed?.()
    }
  }, [initialOrgId])

  const handleSearch = () => setAppliedSearch(searchKeyword)
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  const handleSort = (field: 'fullName' | 'email') => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const SortIcon = ({ field }: { field: 'fullName' | 'email' }) => {
    if (sortField !== field) return <ArrowUpDown className="inline w-3 h-3 ml-1 opacity-40" />
    return sortDirection === 'asc'
      ? <ArrowUp className="inline w-3 h-3 ml-1" />
      : <ArrowDown className="inline w-3 h-3 ml-1" />
  }

  // 조직 목록 (필터 드롭다운용)
  const { data: orgData } = useQuery({
    queryKey: ['adminOrgProgress'],
    queryFn: () => learningReportApi.getOrganizationProgress(),
  })
  const organizations: AdminOrganizationProgress[] = orgData?.data?.data || []

  // 학습자 데이터 로드
  const { data: learnersResponse, isLoading } = useQuery({
    queryKey: ['adminLearnerProgress', appliedSearch, selectedOrgId, selectedStatus],
    queryFn: () => learningReportApi.getLearnerProgress({
      search: appliedSearch || undefined,
      organizationId: selectedOrgId,
      status: selectedStatus === 'all' ? undefined : selectedStatus,
    }),
  })

  const responseData = learnersResponse?.data?.data
  const learners: AdminLearnerProgress[] = responseData?.learners || []
  const totalCount = responseData?.totalCount ?? 0

  // 정렬 적용
  const sortedLearners = useMemo(() => {
    if (!sortField) return learners
    return [...learners].sort((a, b) => {
      const aVal = a[sortField] ?? ''
      const bVal = b[sortField] ?? ''
      const cmp = aVal.localeCompare(bVal, 'ko')
      return sortDirection === 'asc' ? cmp : -cmp
    })
  }, [learners, sortField, sortDirection])

  const handleDetail = (learner: AdminLearnerProgress) => {
    if (!learner.classProductId) {
      toast.warning('이 학습자의 클래스 상품 정보가 없습니다.')
      return
    }
    setSelectedLearner({
      userId: learner.userId,
      classProductId: learner.classProductId,
      fullName: learner.fullName,
      className: learner.className,
    })
  }

  const handleAiqReport = () => {
    toast.info('Post-AIQ 테스트 완료 후 제공될 예정입니다.')
  }

  const statusLabel = (status: string) => {
    switch (status) {
      case 'completed': return '수료'
      case 'in_progress': return '수강중'
      case 'not_started': return '미시작'
      default: return status
    }
  }

  const statusBadgeClass = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700'
      case 'in_progress': return 'bg-blue-100 text-blue-700'
      case 'not_started': return 'bg-gray-100 text-gray-500'
      default: return 'bg-gray-100 text-gray-500'
    }
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="flex gap-2 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="학습자 이름 / 이메일로 검색..."
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className="py-2 pr-4 pl-10 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          />
        </div>
        <button
          onClick={handleSearch}
          className="px-4 py-2 text-sm font-medium text-white bg-amber-500 rounded-lg hover:bg-amber-600 whitespace-nowrap"
        >
          검색
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <select
          value={selectedOrgId ?? ''}
          onChange={(e) => setSelectedOrgId(e.target.value ? Number(e.target.value) : undefined)}
          className="px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
        >
          <option value="">조직 (전체)</option>
          {organizations.map((org) => (
            <option key={org.organizationId} value={org.organizationId}>
              {org.organizationName}
            </option>
          ))}
        </select>

        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
        >
          <option value="all">학습 진행 상태 (전체)</option>
          <option value="not_started">미시작</option>
          <option value="in_progress">수강중</option>
          <option value="completed">수료</option>
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">로딩 중...</div>
        </div>
      ) : (
        <>
          {/* Count Summary */}
          {totalCount > 0 && !appliedSearch && (
            <div className="flex gap-3 items-center px-4 py-3 bg-amber-50 rounded-lg border border-amber-200">
              <span className="text-sm text-amber-700">
                전체 {totalCount}명 중 {learners.length}명 표시
                {selectedOrgId && ` (${organizations.find(o => o.organizationId === selectedOrgId)?.organizationName})`}
              </span>
            </div>
          )}

          {learners.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              {appliedSearch ? '검색 결과가 없습니다.' : '해당 조건의 학습자가 없습니다.'}
            </div>
          ) : (
            <div className="overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap cursor-pointer select-none hover:text-gray-700"
                        onClick={() => handleSort('fullName')}
                      >
                        이름 <SortIcon field="fullName" />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">조직</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">부서/직급</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">클래스</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap">진도율</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap">레슨 정답률</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap">모듈 퀴즈 정답률</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Pre-AIQ</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Post-AIQ</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap">AIQ 성장률</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap">상태</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap">액션</th>
                      <th
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap cursor-pointer select-none hover:text-gray-700"
                        onClick={() => handleSort('email')}
                      >
                        이메일 <SortIcon field="email" />
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {sortedLearners.map((learner, idx) => (
                      <tr key={`${learner.userId}-${learner.classId}-${idx}`} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">{learner.fullName}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{learner.organizationName}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                          {learner.departmentName || '-'} / {learner.position || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{learner.className}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center justify-center space-x-2">
                            <div className="overflow-hidden w-16 h-2 bg-gray-200 rounded-full">
                              <div
                                className="h-full rounded-full bg-amber-500"
                                style={{ width: `${learner.progressPercent}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-500">{learner.progressPercent.toFixed(0)}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-900 whitespace-nowrap">
                          {learner.lessonQuizAccuracy != null ? `${learner.lessonQuizAccuracy.toFixed(1)}%` : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-900 whitespace-nowrap">
                          {learner.checkupQuizAccuracy != null ? `${learner.checkupQuizAccuracy.toFixed(1)}%` : '-'}
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                            learner.preAiqCompleted ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
                          }`}>
                            {learner.preAiqCompleted ? 'O' : 'X'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                            learner.postAiqCompleted ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
                          }`}>
                            {learner.postAiqCompleted ? 'O' : 'X'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-500 whitespace-nowrap">
                          {learner.aiqGrowthRate != null ? `${learner.aiqGrowthRate.toFixed(1)}%` : '-'}
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${statusBadgeClass(learner.learningStatus)}`}>
                            {statusLabel(learner.learningStatus)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center justify-center space-x-1">
                            <button
                              onClick={handleAiqReport}
                              className="px-2 py-1 text-xs font-medium rounded text-amber-600 bg-amber-50 hover:bg-amber-100"
                              title="AIQ 리포트"
                            >
                              AIQ 리포트
                            </button>
                            <button
                              onClick={() => handleDetail(learner)}
                              className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-50 rounded hover:bg-gray-100"
                              title="상세"
                            >
                              상세
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{learner.email}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {selectedLearner && (
        <LearnerProgressDetailModal
          userId={selectedLearner.userId}
          classProductId={selectedLearner.classProductId}
          learnerName={selectedLearner.fullName}
          className={selectedLearner.className}
          isOpen={!!selectedLearner}
          onClose={() => setSelectedLearner(null)}
        />
      )}
    </div>
  )
}
